import React, { useCallback, useState } from 'react';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import { Button, cn } from 'rizzui';
import useFetch from '../hooks/useFetch';
import { UserService } from '../services/user/userService';
import ScorePeriodCard from './ScorePeriodCard';

interface CollapsibleScorePeriodPanelProps {
    userId?: string;
    className?: string;
}

interface ScoreData {
    current: number;
    previous: number;
}

const CollapsibleScorePeriodPanel: React.FC<CollapsibleScorePeriodPanelProps> = ({
    userId,
    className
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const [dailyScores, loadingDaily] = useFetch(useCallback(async () => {
        if (!userId || userId === '' || !isExpanded) return null;
        const response = await (new UserService).showUserScoresComparison(userId, 'daily');
        return response.getResponseData();
    }, [userId, isExpanded]));
    const [weeklyScores, loadingWeekly] = useFetch(useCallback(async () => {
        if (!userId || userId === '' || !isExpanded) return null;
        const response = await (new UserService).showUserScoresComparison(userId, 'weekly');
        return response.getResponseData();
    }, [userId, isExpanded]));
    const [monthlyScores, loadingMonthly] = useFetch(useCallback(async () => {
        if (!userId || userId === '' || !isExpanded) return null;
        const response = await (new UserService).showUserScoresComparison(userId, 'monthly');
        return response.getResponseData();
    }, [userId, isExpanded]));

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div className={cn("bg-white rounded-lg border border-muted mb-6", className)}>
            {/* Header with toggle button */}
            <div className="flex items-center justify-between p-4 border-b border-muted">
                <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Panel de Periodos de Puntuación
                    </h3>
                    <span className="text-sm text-gray-500">
                        (Comparativas por periodo)
                    </span>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleExpanded}
                    className={cn(
                        "flex items-center gap-2 transition-all duration-200 ease-in-out",
                        "hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm",
                        "focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50",
                        isExpanded && "bg-gray-50 border-gray-300"
                    )}
                >
                    <span className="text-sm">
                        {isExpanded ? 'Ocultar' : 'Mostrar'}
                    </span>
                    {isExpanded ? (
                        <IoChevronUp className={cn(
                            "h-4 w-4 transition-transform duration-200 ease-in-out",
                            "transform rotate-0"
                        )} />
                    ) : (
                        <IoChevronDown className={cn(
                            "h-4 w-4 transition-transform duration-200 ease-in-out",
                            "transform rotate-0"
                        )} />
                    )}
                </Button>
            </div>

            {/* Collapsible panel */}
            <div className={cn(
                "overflow-hidden transition-all duration-500 ease-in-out",
                isExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
            )}>
                <div className={cn(
                    "p-4 transition-transform duration-300 ease-in-out",
                    isExpanded ? "transform translate-y-0" : "transform -translate-y-2"
                )}>
                    {userId ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <ScorePeriodCard
                                title="Hoy (vs ayer)"
                                currentScore={dailyScores?.current || 0}
                                previousScore={dailyScores?.previous || 0}
                                isLoading={loadingDaily}
                            />
                            <ScorePeriodCard
                                title="Esta semana (vs semana anterior)"
                                currentScore={weeklyScores?.current || 0}
                                previousScore={weeklyScores?.previous || 0}
                                isLoading={loadingWeekly}
                            />
                            <ScorePeriodCard
                                title="Este mes (vs mes anterior)"
                                currentScore={monthlyScores?.current || 0}
                                previousScore={monthlyScores?.previous || 0}
                                isLoading={loadingMonthly}
                            />
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <p className="text-gray-500">
                                Selecciona un usuario para ver las comparativas de puntuación por periodos
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CollapsibleScorePeriodPanel;